import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { COPYWRITING_PAGE } from "@/data/content";
import { useSound } from "@/lib/SoundContext";
import { TapeLabel, Stamp } from "@/components/Bits";

const INK = "#2B2320";

// frosty ice cube — backdrop for square posts (carousels & statics)
function IceCube() {
  return (
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <g stroke={INK} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round">
        <rect x="8" y="10" width="84" height="82" rx="16" fill="#BFE3F7" />
        <rect x="16" y="18" width="68" height="66" rx="11" fill="#DCF0FB" strokeWidth="2.5" opacity="0.85" />
        <path d="M22 30 q6 -8 16 -9" stroke="#FFF" strokeWidth="5" fill="none" opacity="0.9" />
        <circle cx="72" cy="70" r="3" fill="#FFF" stroke="none" opacity="0.8" />
        <circle cx="30" cy="72" r="2.2" fill="#FFF" stroke="none" opacity="0.7" />
      </g>
    </svg>
  );
}

// ice lolly — backdrop for vertical posts (reels & scripts)
function IceLolly() {
  return (
    <svg viewBox="0 0 100 140" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <g stroke={INK} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round">
        <rect x="42" y="112" width="16" height="24" rx="7" fill="#E8C98F" />
        <rect x="14" y="8" width="72" height="110" rx="26" fill="#BFE3F7" />
        <rect x="22" y="16" width="56" height="94" rx="19" fill="#DCF0FB" strokeWidth="2.5" opacity="0.85" />
        <path d="M30 34 q4 -10 14 -13" stroke="#FFF" strokeWidth="5" fill="none" opacity="0.9" />
        <circle cx="66" cy="86" r="3" fill="#FFF" stroke="none" opacity="0.8" />
      </g>
    </svg>
  );
}

function PostCard({ post, shape, testid }) {
  const { play } = useSound();
  const lolly = shape === "lolly";
  const inner = (
    <motion.div
      data-testid={testid}
      className={`relative flex-none ${lolly ? "w-[250px] h-[442px] sm:w-[300px] sm:h-[530px]" : "w-[300px] h-[300px] sm:w-[380px] sm:h-[380px]"} ${post.link ? "cursor-pointer" : ""}`}
      whileHover={{ scale: 1.05, rotate: -2, transition: { duration: 0.25 } }}
      whileTap={{ scale: 0.95 }}
      onHoverStart={() => play("clink")}
    >
      {post.image ? (
        /* filled slot — clean cover card, no ice shape behind it */
        <div className="absolute inset-0 overflow-hidden rounded-2xl border-[3px] border-ink bg-[#DCF0FB] shadow-[0_10px_0_rgba(43,35,32,0.15)]">
          <img src={post.image} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-60" />
          <img src={post.image} alt={post.title} className="relative w-full h-full object-contain" />
        </div>
      ) : (
        /* empty slot — the ice shape with a placeholder */
        <>
          {lolly ? <IceLolly /> : <IceCube />}
          <div className={`absolute ${lolly ? "left-[14%] right-[14%] top-[7%] bottom-[20.5%]" : "inset-[8%]"}`}>
            <span className="flex items-center justify-center w-full h-full font-hand font-bold text-2xl text-[#2B5E8A]/70 text-center leading-tight px-1">
              [add post]
            </span>
          </div>
        </>
      )}
      {post.link && (
        <span className="absolute -top-2 -right-2 tape !text-[11px] rotate-6">open ↗</span>
      )}
    </motion.div>
  );
  return post.link ? (
    <a href={post.link} target="_blank" rel="noreferrer">{inner}</a>
  ) : (
    inner
  );
}

function PostRow({ row }) {
  const { play } = useSound();
  const scroller = useRef(null);
  const scrollRight = () => {
    play("tick");
    scroller.current?.scrollBy({ left: scroller.current.clientWidth * 0.9, behavior: "smooth" });
  };
  return (
    <section className="mt-10" data-testid={`row-${row.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <TapeLabel className="!text-sm !px-4">{row.label}</TapeLabel>
        <span className="font-hand text-xs text-ink/50">{row.posts.length} posts — scroll →</span>
      </div>
      <div className="relative">
        <div
          ref={scroller}
          className="flex gap-7 overflow-x-auto pb-4 pr-20 items-end scroll-smooth"
          style={{ scrollbarWidth: "none" }}
        >
          {row.posts.map((p, i) => (
            <PostCard key={i} post={p} shape={row.shape} testid={`post-${row.id}-${i}`} />
          ))}
          {/* the right arrow — reveal more */}
          <button
            data-testid={`row-arrow-${row.id}`}
            onClick={scrollRight}
            aria-label={`more ${row.label}`}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-ink text-[#FFF3D6] border-[3px] border-ink shadow-[0_4px_0_rgba(43,35,32,0.3)] font-bold text-2xl cursor-pointer hover:scale-110 active:scale-95 transition-transform"
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
}

export default function FreezerPage() {
  const P = COPYWRITING_PAGE;
  return (
    <div className="min-h-screen pb-24 bg-gradient-to-b from-[#EAF7FF] via-[#F3FBFF] to-[#DDF0FB]" data-testid="freezer-page">
      <div className="max-w-7xl mx-auto px-4 pt-24">
        <Link to="/fridge" data-testid="back-to-fridge" className="font-hand font-bold text-ink/70 hover:text-ink text-lg">
          ← back to the fridge
        </Link>

        {/* context card */}
        <motion.div
          className="paper border-[4px] border-ink rounded-[10px] p-7 sm:p-10 mt-6 -rotate-[0.4deg] shadow-[0_14px_0_rgba(43,35,32,0.15)]"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 130, damping: 15 }}
          data-testid="freezer-context-card"
        >
          <div className="flex flex-wrap items-center gap-3">
            <TapeLabel>{P.kicker}</TapeLabel>
            <Stamp>hand-picked favourites</Stamp>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl mt-3" data-testid="freezer-title">{P.title}</h1>
          <p className="font-hand text-lg sm:text-xl text-ink/80 mt-3 leading-snug max-w-3xl" data-testid="freezer-context">
            {P.context}
          </p>
        </motion.div>

        {/* the rows */}
        {P.rows.map((row, i) => (
          <motion.div
            key={row.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.12, type: "spring", stiffness: 130, damping: 15 }}
          >
            <PostRow row={row} />
          </motion.div>
        ))}

        <p className="font-hand text-center mt-12 text-lg text-ink/60">
          every post opens its instagram — fresh from the freezer.
        </p>
      </div>
    </div>
  );
}
